import React, { useState, useEffect } from 'react';
import { Modal, Select, Spin, Row, Col, Card, Statistic, message, Button, Table, Tag, Tabs, Tooltip } from 'antd';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { analyticsApi, superAdminApi, SMSWithFilterStatus } from '../services/api';
import { MessageOutlined, SyncOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import type { Batch } from '../types';

const { Option } = Select;

interface BatchStatsModalProps {
  visible: boolean;
  batch: Batch | null;
  onClose: () => void;
  isSuperAdmin?: boolean;
}

const BatchStatsModal: React.FC<BatchStatsModalProps> = ({ visible, batch, onClose, isSuperAdmin = false }) => {
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);
  const [messagesByDay, setMessagesByDay] = useState<any[]>([]);
  const [messagesByHour, setMessagesByHour] = useState<any[]>([]);
  const [messagesByCountry, setMessagesByCountry] = useState<any[]>([]);
  const [totalMessages, setTotalMessages] = useState(0);

  // SuperAdmin specific state
  const [recentMessages, setRecentMessages] = useState<SMSWithFilterStatus[]>([]);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [lastSync, setLastSync] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState<string[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lookbackMinutes, setLookbackMinutes] = useState(60);

  useEffect(() => {
    if (visible && batch) {
      loadStats();
      if (isSuperAdmin) {
        loadRecentMessages();
      }
    }
  }, [visible, batch, days, isSuperAdmin]);

  const loadStats = async () => {
    if (!batch) return;

    setLoading(true);
    try {
      const params = {
        days,
        batch_id: batch.id,
      };

      const [dayData, hourData, countryData] = await Promise.all([
        analyticsApi.getMessagesByDay(params),
        analyticsApi.getMessagesByHour(params),
        analyticsApi.getMessagesByCountry(params),
      ]);

      setMessagesByDay(dayData.data);
      setMessagesByHour(hourData.data);
      setMessagesByCountry(countryData.data);

      // Calculate total messages from day data
      const total = dayData.data.reduce((sum: number, item: any) => sum + item.count, 0);
      setTotalMessages(total);
    } catch (error) {
      message.error('Failed to load batch statistics');
    } finally {
      setLoading(false);
    }
  };

  const loadRecentMessages = async () => {
    if (!batch) return;

    setLoadingMessages(true);
    try {
      const data = await superAdminApi.getBatchMessages(batch.id, 50);
      setRecentMessages(data.messages || []);
      setMessagesTotal(data.total);
      setLastSync(data.last_sync);
      setFilterOptions(data.filter_options || []);
    } catch (error) {
      message.error('Failed to load recent messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleSync = async () => {
    if (!batch) return;

    setSyncing(true);
    try {
      const result = await superAdminApi.syncBatch(batch.id, lookbackMinutes);
      message.success(
        `Synced ${result.total_fetched} messages (${result.new_messages} new)`
      );
      // Reload messages and stats after sync
      loadRecentMessages();
      loadStats();
    } catch (error: any) {
      message.error(error.response?.data?.error || 'Failed to sync batch');
    } finally {
      setSyncing(false);
    }
  };

  const messageColumns = [
    {
      title: 'Filter',
      key: 'filterPassed',
      width: 80,
      render: (_: any, record: SMSWithFilterStatus) => (
        record.filterPassed ? (
          <Tooltip title={`Matched: ${record.matchedFilter}`}>
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Pass
            </Tag>
          </Tooltip>
        ) : (
          <Tooltip title="No filter match - hidden from admins/users">
            <Tag color="red" icon={<CloseCircleOutlined />}>
              Fail
            </Tag>
          </Tooltip>
        )
      ),
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: 'To',
      dataIndex: 'to',
      key: 'to',
      width: 140,
    },
    {
      title: 'Content',
      dataIndex: 'content',
      key: 'content',
      ellipsis: true,
    },
    {
      title: 'Received',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 160,
      render: (text: string) => new Date(text).toLocaleString(),
    },
  ];

  const renderChartsTab = () => (
    <>
      {/* Header with time period selector and total messages */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={12}>
          <Card bordered={false}>
            <Statistic
              title="Total Messages"
              value={totalMessages}
              prefix={<MessageOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col span={12}>
          <div style={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <div style={{ width: '100%' }}>
              <label style={{ marginRight: 8, fontWeight: 500 }}>Time Period:</label>
              <Select value={days} onChange={setDays} style={{ width: 150 }}>
                <Option value={7}>Last 7 days</Option>
                <Option value={30}>Last 30 days</Option>
                <Option value={90}>Last 90 days</Option>
              </Select>
            </div>
          </div>
        </Col>
      </Row>

      {/* Messages by Day */}
      <Card title="Messages Per Day" bordered={false} style={{ marginBottom: 16 }}>
        <ResponsiveContainer width="100%" height={250}>
          <LineChart data={messagesByDay}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" style={{ fontSize: 12 }} />
            <YAxis />
            <RechartsTooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#1890ff" strokeWidth={2} name="Messages" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Messages by Hour and Country */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Messages Per Hour (24h)" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={messagesByHour}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="count" fill="#52c41a" name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col span={12}>
          <Card title="Messages Per Country" bordered={false}>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={messagesByCountry}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="country" />
                <YAxis />
                <RechartsTooltip />
                <Legend />
                <Bar dataKey="count" fill="#722ed1" name="Messages" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </>
  );

  // Calculate filter stats
  const passedCount = recentMessages.filter(m => m.filterPassed).length;
  const failedCount = recentMessages.length - passedCount;

  const renderMessagesTab = () => (
    <>
      {/* Sync Controls */}
      <Card bordered={false} style={{ marginBottom: 16 }}>
        <Row gutter={16} align="middle">
          <Col span={6}>
            <Statistic
              title="Total Messages in DB"
              value={messagesTotal}
              prefix={<MessageOutlined />}
            />
          </Col>
          <Col span={6}>
            <div>
              <div style={{ color: '#888', marginBottom: 4 }}>Last Sync</div>
              <div style={{ fontSize: 16 }}>
                <ClockCircleOutlined style={{ marginRight: 8 }} />
                {lastSync ? new Date(lastSync).toLocaleString() : 'Never'}
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div>
              <div style={{ color: '#888', marginBottom: 4 }}>Filter Status (shown)</div>
              <div>
                <Tag color="green">{passedCount} Pass</Tag>
                <Tag color="red">{failedCount} Fail</Tag>
              </div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Select
                value={lookbackMinutes}
                onChange={setLookbackMinutes}
                style={{ width: 130 }}
              >
                <Option value={15}>Last 15 min</Option>
                <Option value={30}>Last 30 min</Option>
                <Option value={60}>Last 1 hour</Option>
                <Option value={360}>Last 6 hours</Option>
                <Option value={1440}>Last 24 hours</Option>
              </Select>
              <Button
                type="primary"
                icon={<SyncOutlined spin={syncing} />}
                onClick={handleSync}
                loading={syncing}
              >
                Sync Now
              </Button>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Active Filter Options */}
      {filterOptions.length > 0 && (
        <Card size="small" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 500 }}>Active Filters:</span>
            {filterOptions.map((option, idx) => (
              <Tag key={idx} color="blue">{option}</Tag>
            ))}
          </div>
        </Card>
      )}

      {/* Recent Messages Table */}
      <Card title="Recent Messages" bordered={false}>
        <Table
          dataSource={recentMessages}
          columns={messageColumns}
          rowKey="messageId"
          loading={loadingMessages}
          size="small"
          pagination={{ pageSize: 10 }}
          scroll={{ y: 400 }}
        />
      </Card>
    </>
  );

  const tabItems = [
    {
      key: 'charts',
      label: 'Analytics',
      children: renderChartsTab(),
    },
  ];

  if (isSuperAdmin) {
    tabItems.push({
      key: 'messages',
      label: 'Messages & Sync',
      children: renderMessagesTab(),
    });
  }

  return (
    <Modal
      title={
        <div>
          <div style={{ fontSize: 18, fontWeight: 600 }}>Batch Statistics</div>
          <div style={{ fontSize: 14, color: '#888', fontWeight: 400, marginTop: 4 }}>
            {batch?.name} ({batch?.country})
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      style={{ top: 20 }}
    >
      {loading && !isSuperAdmin ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      ) : (
        <Tabs items={tabItems} defaultActiveKey="charts" />
      )}
    </Modal>
  );
};

export default BatchStatsModal;
